import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import Logo from "./Logo";
import { SignOut, House, ScanSmiley, Stack, UsersThree, WarningCircle, X as XIcon } from "@phosphor-icons/react";

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);

  const items = [
    { to: "/dashboard", label: "My Events", icon: House, testid: "nav-dashboard" },
    { to: "/member", label: "Member Portal", icon: UsersThree, testid: "nav-member" },
    { to: "/scanner", label: "Scanner", icon: ScanSmiley, testid: "nav-scanner" },
    { to: "/ledger", label: "Audit Ledger", icon: Stack, testid: "nav-ledger" },
  ];

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const resendVerify = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent");
      setDismissed(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setResending(false);
    }
  };

  const showUnverifiedBanner = user && user.email_verified === false && !dismissed;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="sticky top-0 z-40 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3 group" data-testid="brand-home">
            <Logo size={40} />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {items.map((it) => {
              const Icon = it.icon;
              const active = location.pathname.startsWith(it.to);
              return (
                <Link
                  key={it.to}
                  to={it.to}
                  data-testid={it.testid}
                  className={`flex items-center gap-2 px-4 py-2 mono-font text-xs uppercase tracking-[0.2em] transition-colors duration-200 ${
                    active ? "text-[#00FF66] bg-white/5 border border-white/10" : "text-white/60 hover:text-white border border-transparent"
                  }`}
                >
                  <Icon size={14} weight={active ? "fill" : "regular"} />
                  {it.label}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <div className="text-xs text-white/90 mono-font">{user?.name}</div>
              <div className="text-[10px] text-white/40 mono-font truncate max-w-[160px]">{user?.email}</div>
            </div>
            <button
              onClick={onLogout}
              data-testid="logout-btn"
              className="flex items-center gap-2 px-3 py-2 border border-white/15 mono-font text-xs uppercase tracking-[0.2em] text-white/70 hover:text-white hover:border-white/40 transition-colors duration-200"
            >
              <SignOut size={14} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
        <div className="md:hidden border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-2 flex">
            {items.map((it) => {
              const Icon = it.icon;
              const active = location.pathname.startsWith(it.to);
              return (
                <Link key={it.to} to={it.to} data-testid={`${it.testid}-mobile`}
                  className={`flex-1 flex flex-col items-center py-2 mono-font text-[10px] uppercase tracking-widest ${active ? "text-[#00FF66]" : "text-white/50"}`}>
                  <Icon size={16} weight={active ? "fill" : "regular"} />
                  {it.label}
                </Link>
              );
            })}
          </div>
        </div>
      </header>
      {showUnverifiedBanner && (
        <div className="bg-[#FF9900]/10 border-b border-[#FF9900]/40" data-testid="unverified-banner">
          <div className="max-w-[1400px] mx-auto px-6 py-3 flex flex-wrap items-center gap-3">
            <WarningCircle size={16} weight="fill" className="text-[#FF9900]" />
            <div className="mono-font text-xs text-[#FF9900] flex-1 min-w-0">
              Verify your email address to confirm your account. Check <span className="text-white">{user?.email}</span> for the link.
            </div>
            <button onClick={resendVerify} disabled={resending} data-testid="resend-verify-btn"
              className="px-3 py-1.5 border border-[#FF9900]/60 text-[#FF9900] mono-font text-[10px] uppercase tracking-widest hover:bg-[#FF9900] hover:text-black transition-colors duration-200 disabled:opacity-50">
              {resending ? "Sending…" : "Resend Email"}
            </button>
            <button onClick={() => setDismissed(true)} data-testid="dismiss-verify-btn"
              className="p-1.5 border border-white/15 text-white/60 hover:text-white transition-colors duration-200">
              <XIcon size={12} />
            </button>
          </div>
        </div>
      )}
      <main className="max-w-[1400px] mx-auto px-6 py-10">{children}</main>
    </div>
  );
}
